<script lang="ts">
/**
 * FormSelect - A native select element for forms
 * Styled to match TextInput and other form fields
 * Unlike the custom Select component, this uses native <select> for better form compatibility
 *
 * When `label` is provided, renders with floating label that animates on focus/selection
 * (matching InputContainer's behaviour)
 */

interface SelectOption {
    value: string;
    label: string;
}

let {
    name = '',
    id = '',
    label = '',
    placeholder = 'Select an option',
    options = [] as (SelectOption | string)[],
    required = false,
    value = $bindable(''),
    isInvalid = false,
    inputElement = $bindable(),
    disabled = false,
    ...rest
} = $props();

// Helper to normalise options to {value, label} format
function normaliseOption(option: SelectOption | string): SelectOption {
    if (typeof option === 'string') {
        return { value: option, label: option };
    }
    return option;
}

// Auto-generate a stable ID when label is provided but id is not (for accessibility)
$effect(() => {
    if (label && !id) {
        console.warn(
            `FormSelect component with label "${label}" is missing a required "id" prop. This is needed for accessibility.`,
        );
    }
});
const selectId = $derived(id);
</script>

{#if label}
    <div class="select-container" class:select-container--has-value={value}>
        <label for={selectId}>{label}</label>
        <select
            class="field"
            class:field--invalid={isInvalid}
            class:field--placeholder={!value}
            {name}
            id={selectId}
            {required}
            {disabled}
            bind:value
            bind:this={inputElement}
            {...rest}
        >
            <!-- Empty placeholder when using floating label (label acts as placeholder) -->
            <option value="" disabled></option>
            {#each options as option}
                {@const normalised = normaliseOption(option)}
                <option value={normalised.value}>{normalised.label}</option>
            {/each}
        </select>
    </div>
{:else}
    <select
        class="field"
        class:field--invalid={isInvalid}
        class:field--placeholder={!value}
        {name}
        {id}
        {required}
        {disabled}
        bind:value
        bind:this={inputElement}
        {...rest}
    >
        <option value="" disabled>{placeholder}</option>
        {#each options as option}
            {@const normalised = normaliseOption(option)}
            <option value={normalised.value}>{normalised.label}</option>
        {/each}
    </select>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/fonts';
    @use 'sass-mq/mq' as *;

    .select-container {
        position: relative;

        label {
            color: var(--colour-dreamless-sleep);
            font-family: fonts.$fonts-secondary;
            font-size: 1.6rem;
            font-weight: fonts.$fonts-weight--normal;
            line-height: 1.6;
            pointer-events: none;
            position: absolute;
            top: 0;
            transition:
                font-size var(--timing-duration-short),
                top var(--timing-duration-short);
        }

        &--has-value label,
        &:focus-within label {
            color: var(--colour-squant);
            font-size: 1.1rem;
            text-transform: uppercase;
            top: -1.2rem;
        }

        .field {
            margin-bottom: 3rem;

            @include mq($from: tablet) {
                margin-bottom: 4rem;
            }
        }
    }

    .field {
        appearance: none;
        background: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
        background-position: right 0 center;
        background-repeat: no-repeat;
        border-color: var(--colour-squant);
        border-radius: 0;
        border-style: solid;
        border-width: 0 0 1px;
        color: var(--colour-dreamless-sleep);
        cursor: pointer;
        font-family: fonts.$fonts-secondary;
        font-size: 1.6rem;
        font-weight: fonts.$fonts-weight--normal;
        line-height: 1.6;
        margin: 0 0 3rem;
        padding-right: 2rem;
        width: 100%;

        @include mq($from: tablet) {
            margin: 0 0 4rem;
        }

        &:focus {
            border-color: var(--colour-lucky-grey);
            outline: none;
        }

        &:disabled {
            background-color: var(--colour-crisp-muslin);
            cursor: not-allowed;
            opacity: 0.7;
        }

        &:invalid {
            box-shadow: none;
        }

        &--invalid {
            border-color: var(--colour-sacrifice-altar) !important;
        }

        &--placeholder {
            color: var(--colour-squant);
        }

        // Hide default arrow in IE/Edge
        &::-ms-expand {
            display: none;
        }

        option {
            color: var(--colour-dreamless-sleep);
        }

        option:disabled {
            color: var(--colour-squant);
        }
    }
</style>
